import { useEffect } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import { usePolicyPauseOverviewInfo } from '@/dashboard/components/Products/policyCard/hooks/usePolicyPauseOverviewInfo';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { DatePickerControlled } from '@/dashboard/mui/form/DatePicker';
import { PencilIcon } from '@heroicons/react/outline';
import {
  calculateTimeBetweenDates,
  judgeWhetherIsLaterThanTheDate,
} from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import PolicyPauseDayInfoView from './policyPauseDayInfo';
import { PausePolicyHandleType } from './types';
import PausePolicyWarningInfo from './warningInfo';

type UnPausePolicyFormViewProps = CustomDialogProps & {
  policy: PolicyModel;
  info: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
  confirm: (info: { startDate: Dayjs; endDate: Dayjs }) => void;
  pauseType?: PausePolicyHandleType;
};

const UnPausePolicyFormView = (props: UnPausePolicyFormViewProps) => {
  const {
    className,
    policy,
    close,
    confirm,
    info: { startDate, endDate } = {},
    pauseType,
  } = props;
  const methods = useForm({
    defaultValues: {
      startDate:
        startDate ||
        policy.allowHandleStartDateDayjs.add(1, 'day').startOf('day'),
      endDate: endDate || policy.allowHandleStartDateDayjs.add(31, 'day'),
    },
  });
  const { handleSubmit, register, control, getValues, trigger, watch } =
    methods;
  const { timeInfoLoading, policyRemaingTimeInfo } = usePolicyPauseOverviewInfo(
    policy.id
  );
  const isEditPause = pauseType === 'edit';

  const startDateWatch = watch('startDate');
  const endDateWatch = watch('endDate');
  useEffect(() => {
    trigger(['startDate', 'endDate']);
  }, [startDateWatch, endDateWatch]);

  const checkFormSuccess = () => {
    if (confirm) {
      confirm({
        startDate: getValues('startDate'),
        endDate: getValues('endDate'),
      });
    }
  };

  const unPauseAction = () => {
    handleSubmit(checkFormSuccess)();
  };

  const closeAction = () => {
    if (close) close();
  };

  const startDateValidate = (time) => {
    // start time > end time
    if (judgeWhetherIsLaterThanTheDate(time, getValues('endDate')))
      return 'Start date must be earlier than end date';

    return true;
  };

  const endDateValidate = (time) => {
    // end time < polict end time
    if (judgeWhetherIsLaterThanTheDate(time, policy.policyEndDateDayjs))
      return 'End date must be earlier than policy end date';

    // end time - start time >= 30
    const days = calculateTimeBetweenDates(
      time.startOf('day'),
      getValues('startDate')
    );
    return days >= 30 ? true : 'Must be at least 30 days from the start date';
  };
  const today = dayjs();
  const maxPauseDate = today.add(policyRemaingTimeInfo?.days_left, 'day');

  return (
    <FormProvider {...methods}>
      <form className="relative">
        {timeInfoLoading && <LogoLoading />}
        <Box className={clsx('flex flex-col items-center', className)}>
          <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
            <PencilIcon className="h-7 w-7" />
          </Box>
          <Typography
            className="text-black-secondary mt-4 font-black"
            variant="h5"
          >
            {isEditPause ? 'Edit pause' : 'Pause insurance'}
          </Typography>
          {!isEditPause && (
            <Typography
              className="text-black-secondary mt-3 text-center"
              variant="subtitle1"
            >
              Pausing your policy when you are not working reduces your premium
              payment and ensures your work during the active dates are
              continuously protected.{' '}
              <a
                href="https://jove5095.zendesk.com/hc/en-us/search?content_tags=01J2XW9D0SN65SDNG3WH1X8M36&filter_by=knowledge_base&utf8=%E2%9C%93"
                target="_blank"
                className="text-brand-primary"
              >
                Learn more.
              </a>
            </Typography>
          )}
          <PolicyPauseDayInfoView
            className="mt-7"
            info={policyRemaingTimeInfo}
            type={2}
          />
          <Typography
            className="text-black-secondary mt-4 font-bold"
            variant="subtitle1"
          >
            When would you like to pause your policy?
          </Typography>
          <Box className="mt-5 flex w-full flex-row">
            <DatePickerControlled
              name="startDate"
              control={control}
              {...register('startDate', {
                required: true,
                validate: startDateValidate,
              })}
              minDate={policy.allowHandleStartDateDayjs.add(1, 'day')}
              maxDate={policy.policyEndDateDayjs}
              label="Start date"
              disabled
            />
            <DatePickerControlled
              className="ml-2"
              name="endDate"
              control={control}
              {...register('endDate', {
                required: true,
                validate: endDateValidate,
              })}
              minDate={policy.allowHandleStartDateDayjs.add(1, 'day')}
              maxDate={maxPauseDate}
              label="End date"
            />
          </Box>
          {!isEditPause && <PausePolicyWarningInfo />}

          <Box className="mt-6 flex w-full flex-row justify-end">
            <Button
              className="text-brand-primary text-lg font-bold"
              variant="text"
              onClick={closeAction}
            >
              Cancel
            </Button>
            <LoadingButton
              className="ml-2 h-12 w-[174px] rounded-full text-lg font-bold"
              color="primary"
              onClick={unPauseAction}
              loading={timeInfoLoading}
              loadingIndicator="Loading..."
              variant="contained"
            >
              <span>Next</span>
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default UnPausePolicyFormView;
